/**
 * Contact-field validation shared by the demo request form (browser) and /api/submit-form (server), so the two can never disagree.
 * Owner ruling 2026-10-08: the form only counted ten digits and checked for an @; junk like (000) 000-0000 or a..b@x.com went through,
 * and the server only checked that the fields were not empty.
 *
 * Phone = a North American number (NANP), 10 digits after an optional leading 1:
 *   area code   first digit 2-9, and not N11 (211, 311 ... 911)
 *   exchange    first digit 2-9, and not N11
 *   never       one digit repeated ten times (5555555555), a straight run (2345678901, 9876543210), or the fictional 555-01xx block.
 * Email = a plain ASCII address: one @, a local part of 1-64 characters with no leading, trailing or doubled dot, and a domain of
 * dot-separated labels (1-63 characters, letters / digits / hyphen, no leading or trailing hyphen) ending in an alphabetic TLD of 2+
 * letters (or an xn-- punycode TLD), 254 characters at most, no spaces.
 */

const LOCAL_OK = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
const LABEL_OK = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;
const TLD_OK = /^(?:[A-Za-z]{2,}|xn--[A-Za-z0-9-]{2,59})$/;

export function validateEmail(value: string): boolean {
  if (typeof value !== 'string') return false;
  const v = value; // not trimmed on purpose: a trailing space would be stored with the lead, so it stays invalid, as before
  if (!v || v.length > 254 || /\s/.test(v)) return false;
  const at = v.indexOf('@');
  if (at < 1 || at !== v.lastIndexOf('@')) return false;
  const local = v.slice(0, at);
  const domain = v.slice(at + 1);
  if (local.length > 64 || !LOCAL_OK.test(local)) return false;
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  const labels = domain.split('.');
  if (labels.length < 2) return false;
  if (!labels.every((l) => LABEL_OK.test(l))) return false;
  return TLD_OK.test(labels[labels.length - 1]);
}

/** The ten digits of a US/Canada number: strips punctuation and one leading country code 1. Returns '' when it is not ten digits. */
export function phoneDigits(value: string): string {
  if (typeof value !== 'string') return '';
  let d = value.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d.length === 10 ? d : '';
}

export function validatePhone(value: string): boolean {
  const d = phoneDigits(value);
  if (!d) return false;
  const npa = d.slice(0, 3);
  const nxx = d.slice(3, 6);
  const line = d.slice(6);
  if (!/[2-9]/.test(npa[0]) || !/[2-9]/.test(nxx[0])) return false;
  if (npa.slice(1) === '11' || nxx.slice(1) === '11') return false;
  if (/^(\d)\1{9}$/.test(d)) return false;
  if ('01234567890123456789'.includes(d) || '98765432109876543210'.includes(d)) return false;
  if (nxx === '555' && /^01\d\d$/.test(line)) return false;
  return true;
}

export const EMAIL_ERROR = 'Enter a valid email, like name@company.com.';
export const PHONE_ERROR = 'Enter a valid 10-digit US phone number.';
